import { useCallback, useEffect, useRef, useState } from 'react';
import { useDataProvider } from 'react-admin';
import hasPublishingCourse from './hasPublishingCourse';

const POLL_INTERVAL_MS = 10000;
const GITHUB_WATCH_DURATION_MS = 5 * 60 * 1000;

/**
 * Background cohort refetch for publication status without Show-level flicker.
 * Uses dataProvider.getOne and keeps the previous record visible on failure.
 *
 * Polls while any course is `publishing`, or for up to 5 minutes after a
 * GitHub button click. Refetches immediately on window focus / tab visible
 * while watching or publishing. Skips overlapping getOne calls.
 */
const useCoursePublicationWatch = ({ record, resource }) => {
  const dataProvider = useDataProvider();
  const [liveRecord, setLiveRecord] = useState(record);
  const [watchUntil, setWatchUntil] = useState(null);
  const liveRecordRef = useRef(record);
  const watchUntilRef = useRef(null);
  const refetchInFlightRef = useRef(false);

  useEffect(() => {
    setLiveRecord(record);
    liveRecordRef.current = record;
  }, [record]);

  useEffect(() => {
    watchUntilRef.current = watchUntil;
  }, [watchUntil]);

  useEffect(() => {
    liveRecordRef.current = liveRecord;
  }, [liveRecord]);

  const refetchInBackground = useCallback(async () => {
    const cohortId = record && (record.id || record._id);
    if (!cohortId || !resource || !dataProvider) {
      return;
    }
    if (refetchInFlightRef.current) {
      return;
    }

    refetchInFlightRef.current = true;
    try {
      const { data } = await dataProvider.getOne(resource, { id: cohortId });
      if (data) {
        setLiveRecord((previous) => ({
          ...(previous || {}),
          ...data,
        }));
      }
    } catch (error) {
      // Keep previous UI visible; do not clear the tab on background failure.
      if (process.env.NODE_ENV === 'development') {
        // eslint-disable-next-line no-console
        console.warn('Background cohort publication refetch failed', error);
      }
    } finally {
      refetchInFlightRef.current = false;
    }
  }, [dataProvider, record, resource]);

  const onGithubOpen = useCallback(() => {
    setWatchUntil(Date.now() + GITHUB_WATCH_DURATION_MS);
    refetchInBackground();
  }, [refetchInBackground]);

  const shouldKeepPolling = useCallback(() => {
    const publishing = hasPublishingCourse(liveRecordRef.current);
    const until = watchUntilRef.current;
    const watching = until != null && Date.now() < until;
    return publishing || watching;
  }, []);

  useEffect(() => {
    if (!shouldKeepPolling()) {
      return undefined;
    }

    const intervalId = setInterval(() => {
      if (!shouldKeepPolling()) {
        if (
          watchUntilRef.current != null &&
          Date.now() >= watchUntilRef.current
        ) {
          setWatchUntil(null);
        }
        return;
      }
      refetchInBackground();
    }, POLL_INTERVAL_MS);

    return () => clearInterval(intervalId);
  }, [liveRecord, watchUntil, refetchInBackground, shouldKeepPolling]);

  useEffect(() => {
    const maybeRefetch = () => {
      if (shouldKeepPolling()) {
        refetchInBackground();
      }
    };

    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        maybeRefetch();
      }
    };

    window.addEventListener('focus', maybeRefetch);
    document.addEventListener('visibilitychange', onVisibilityChange);

    return () => {
      window.removeEventListener('focus', maybeRefetch);
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, [refetchInBackground, shouldKeepPolling]);

  return {
    liveRecord: liveRecord || record,
    onGithubOpen,
  };
};

export default useCoursePublicationWatch;
